import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Form } from "antd";
import styled, { css } from "styled-components";

import {
  WizardContainer,
  Button,
  ButtonContainer,
  WizardSteps,
  WizardStep,
  FormContainer,
  Sidebar,
  Topbar,
  MobileWizardStepsContainer,
  MobileWizardSteps,
  MobileButtonContainer,
} from "../wizard/wizard.styled";
import bagong_pilipinas_sidebar from "../../../assets/bagong_pilipinas_sidebar.png";
import { LogoImg } from "components/logoImg";
import { ENTITIES, entity } from "constant/entity";
import { mobile, tablet } from "utils/styles";
import SimilarResults from "components/similarResults/SimilarResults";

const propsToIgnore = ["isCard", "isVisible"];
const styledProp = {
  shouldForwardProp: props => !propsToIgnore.includes(props),
};
const StepContent = styled("div", styledProp)`
  ${({ isCard = false }) =>
    isCard &&
    mobile`
      border-radius: 50px 50px 0 0;
      height: calc(100vh - 200px);
      background-color: #fff;
      position: relative;
      z-index: 2;
      box-shadow:
        0 -4px 6px -1px rgba(0, 0, 0, 0.1),
        0 -2px 4px -1px rgba(0, 0, 0, 0.06);
    `}
`;
const StyledButtonContainer = styled(ButtonContainer)``;
const StyledMobileWizardStepsContainer = styled(MobileWizardStepsContainer, styledProp)`
  ${({ isVisible }) =>
    !isVisible &&
    css`
      display: none;
    `}
`;
const SidebarLogo = styled(LogoImg)`
  align-self: center;
  margin-top: 20px;

  ${tablet`
    max-width: 150px;
  `}

  ${entity === ENTITIES.AKAP_BIGAS &&
  css`
    padding: 0;
    width: 100%;
    > img {
      max-width: 200px;
      width: 100%;
    }
  `}
`;

const WizardForm = ({
  steps,
  disableClick = true,
  setCurrentStep: setCurrentStepFromProps,
  currentStepFromProps,
  isIdCardStep = false,
  isHidden = false,
  resetFormData = false,
}) => {
  const [currentStep, setCurrentStep] = useState(currentStepFromProps || 0);
  const [formData, setFormData] = useState({});
  const [form] = Form.useForm();
  const formRef = useRef();
  const [, setStepValidationStatus] = useState({});
  const [isStepValid, setIsStepValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const hasBackButton = currentStep > 0 && !steps[currentStep].hideBackBtn;
  const hasNextButton = !steps[currentStep].hideNextBtn;
  const hasSteps = !steps[currentStep].hideWizardSteps;

  useEffect(() => {
    // Initialize validation status for each step
    const initialValidationStatus = steps.reduce(
      (acc, step, index) => ({
        ...acc,
        [index]: true,
      }),
      {},
    );
    setStepValidationStatus(initialValidationStatus);
  }, [steps]);

  // Handle form data reset
  useEffect(() => {
    if (resetFormData) {
      setFormData({});
      form.resetFields();
    }
  }, [resetFormData]);

  const handleInputChange = () => {
    setFormData(prevFormData => ({
      ...prevFormData,
    }));
  };

  const handleStepClick = step => {
    setCurrentStep(step);
  };

  const handleNextStep = async () => {
    try {
      const isValid = await form.validateFields();
      setIsStepValid(isValid);
      if (isValid) {
        const asyncValidator = steps[currentStep]?.asyncValidator;
        const asyncFn = steps[currentStep]?.asyncFn;
        if (asyncFn) {
          setIsLoading(true);
          await asyncFn();
          setIsLoading(false);
        }
        if (asyncValidator) {
          setIsLoading(true);
          const asyncValidatorResult = await asyncValidator();
          setIsLoading(false);
          if (asyncValidatorResult === false) {
            return; // Do not proceed to the next step
          }
        }
        setCurrentStep(prevStep => prevStep + 1);
      }
    } catch (error) {
      console.log("form validation failed:", error);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(prevStep => prevStep - 1);
  };

  const handleFormChange = () => {
    // Update step validation status on form change
    const currentStepErrors = form.getFieldsError().some(field => field.errors.length > 0);
    setIsStepValid(!currentStepErrors);
    setStepValidationStatus(prevStatus => ({
      ...prevStatus,
      [currentStep]: !currentStepErrors,
    }));
  };

  const renderStepContent = () => {
    const StepComponent = steps[currentStep].component;

    return (
      <StepContent className="step-component-container" isCard={isIdCardStep}>
        <FormContainer hidden={isHidden} form={form} ref={formRef} onFieldsChange={handleFormChange}>
          {!steps[currentStep].hideTitle && (
            <Topbar className="step-component-title">
              <p style={{ margin: steps[currentStep].centerTitle ? "0 auto" : null }}>{steps[currentStep].title}</p>
            </Topbar>
          )}
          <StepComponent form={form} formData={formData} onInputChange={handleInputChange} />
          <StyledButtonContainer>
            {hasBackButton && (
              <Button
                type={steps[currentStep].isSocial ? "social-service" : "primary"}
                onClick={handlePreviousStep}
                disabled={isLoading}>
                {isLoading ? "Loading..." : "Back"}
              </Button>
            )}
            {hasNextButton && (
              <Button
                type={steps[currentStep].isSocial ? "social-service" : "primary"}
                onClick={handleNextStep}
                disabled={!isStepValid || isLoading}>
                {isLoading ? "Loading..." : steps[currentStep]?.btnNextOverwriteTitle || "Next"}
              </Button>
            )}
          </StyledButtonContainer>
        </FormContainer>
        {isHidden && <SimilarResults />}
      </StepContent>
    );
  };

  // Notify parent when internal step changes
  useEffect(() => {
    setCurrentStepFromProps?.(currentStep);
  }, [currentStep, setCurrentStepFromProps]);

  // Listen for external step changes from parent
  useEffect(() => {
    if (currentStepFromProps !== undefined && currentStepFromProps !== currentStep) {
      setCurrentStep(currentStepFromProps);
    }
  }, [currentStepFromProps]);

  return (
    <WizardContainer>
      <Sidebar>
        <SidebarLogo />
        <WizardSteps
          current={currentStep}
          size="small"
          onChange={handleStepClick}
          direction="vertical"
          responsive={false}>
          {steps.map((step, index) => (
            <WizardStep key={index} title={step.title} disabled={disableClick} />
          ))}
        </WizardSteps>
        {entity === ENTITIES.BPSF ? (
          <img className="bg" src={bagong_pilipinas_sidebar} />
        ) : entity === ENTITIES.AKAP_BIGAS ? (
          <img className="bg" src="/static/akapbigas/logo-footer.svg" />
        ) : (
          <div />
        )}
      </Sidebar>
      {renderStepContent()}
      <StyledMobileWizardStepsContainer isVisible={hasBackButton || hasNextButton || hasSteps}>
        <MobileButtonContainer>
          {hasBackButton && (
            <Button className="ant-btn-default" onClick={handlePreviousStep} disabled={isLoading}>
              {isLoading ? "Loading..." : "Back"}
            </Button>
          )}
          {hasNextButton && (
            <Button
              type={steps[currentStep].isSocial ? "social-service" : "primary"}
              onClick={handleNextStep}
              disabled={!isStepValid || isLoading}>
              {isLoading ? "Loading..." : steps[currentStep]?.btnNextOverwriteTitle || "Next"}
            </Button>
          )}
        </MobileButtonContainer>
        {hasSteps && (
          <MobileWizardSteps
            className="wizard-ant-steps"
            current={currentStep}
            size="small"
            onChange={handleStepClick}
            direction="horizontal"
            responsive={false}>
            {steps.map((step, index) => (
              <WizardStep key={index} disabled={disableClick} />
            ))}
          </MobileWizardSteps>
        )}
      </StyledMobileWizardStepsContainer>
    </WizardContainer>
  );
};

WizardForm.propTypes = {
  steps: PropTypes.array,
  onSubmit: PropTypes.func,
  disableClick: PropTypes.bool,
  setCurrentStep: PropTypes.func,
  currentStepFromProps: PropTypes.number,
  isIdCardStep: PropTypes.bool,
  isHidden: PropTypes.bool,
  resetFormData: PropTypes.bool,
};

export default WizardForm;
